// ----------------------------------------------------------------------------
// playback-engine — Web Audio scheduling for DAWSEUR
//
// Manages:
//   - AudioContext + master gain
//   - Per-track gain nodes (volume)
//   - Per-track stereo panner nodes
//   - Scheduling of audio buffers at time positions
//   - Time-stretch via SoundTouch WSOLA (pitch-preserving, offline pre-process)
//   - Pitch-shift via detune
//   - Transport state (playing, position, loop region)
// ----------------------------------------------------------------------------

const { SoundTouch, SimpleFilter, WebAudioBufferSource } = require('soundtouchjs');
const { decodeAIFF, isAIFF } = require('../aiff-decoder.js');

// ---- Offline time-stretch using SoundTouch WSOLA ----
// Pre-processes a buffer through WSOLA to create a stretched version
// with pitch preserved. Returns a new AudioBuffer.
// stretchRatio > 1 = longer output, < 1 = shorter output
function stretchBufferOffline(ctx, inputBuffer, stretchRatio) {
   if (!inputBuffer || !inputBuffer.duration || stretchRatio <= 0) return inputBuffer;

   // For near-1.0 ratios, just return the original (no processing needed)
   if (stretchRatio > 0.97 && stretchRatio < 1.03) return inputBuffer;

   const source = new WebAudioBufferSource(inputBuffer);
   const soundTouch = new SoundTouch();
   // tempo = 1/stretchRatio: ratio>1 (longer) → tempo<1 (slower)
   soundTouch.tempo = 1 / stretchRatio;
   soundTouch.pitch = 1.0; // preserve pitch

   const filter = new SimpleFilter(source, soundTouch);

   const sampleRate = inputBuffer.sampleRate;
   const numChannels = inputBuffer.numberOfChannels;
   const estimatedOutputFrames = Math.floor(inputBuffer.length * stretchRatio + 1024);

   // Extract samples in chunks
   const chunkSize = 4096;
   const allSamples = []; // array of Float32Array chunks (interleaved stereo)
   let totalFrames = 0;

   while (true) {
      const samples = new Float32Array(chunkSize * 2);
      const extracted = filter.extract(samples, chunkSize);
      if (extracted === 0) break;
      // Store only the extracted portion
      if (extracted < chunkSize) {
         allSamples.push(samples.slice(0, extracted * 2));
      } else {
         allSamples.push(samples);
      }
      totalFrames += extracted;
   }

   if (totalFrames === 0) return inputBuffer;

   // Create output buffer
   const outputBuffer = ctx.createBuffer(numChannels, totalFrames, sampleRate);
   const outLeft = outputBuffer.getChannelData(0);
   const outRight = numChannels > 1 ? outputBuffer.getChannelData(1) : outputBuffer.getChannelData(0);

   // De-interleave
   let frameIdx = 0;
   for (const chunk of allSamples) {
      const chunkFrames = chunk.length / 2;
      for (let i = 0; i < chunkFrames && frameIdx < totalFrames; i++) {
         outLeft[frameIdx] = chunk[i * 2];
         outRight[frameIdx] = chunk[i * 2 + 1];
         frameIdx++;
      }
   }

   return outputBuffer;
}

function createPlaybackEngine() {
   const ctx = new (window.AudioContext || window.webkitAudioContext)();

   // ---- Master chain: masterEQ → masterPan → masterGain → masterAnalyser → destination ----
   const masterGain = ctx.createGain();
   masterGain.gain.value = 0.5;
   const masterPan = ctx.createStereoPanner();
   const masterEqLow = ctx.createBiquadFilter();
   masterEqLow.type = 'lowshelf';
   masterEqLow.frequency.value = 200;
   masterEqLow.gain.value = 0;
   const masterEqMid = ctx.createBiquadFilter();
   masterEqMid.type = 'peaking';
   masterEqMid.frequency.value = 1000;
   masterEqMid.Q.value = 1;
   masterEqMid.gain.value = 0;
   const masterEqHi = ctx.createBiquadFilter();
   masterEqHi.type = 'highshelf';
   masterEqHi.frequency.value = 5000;
   masterEqHi.gain.value = 0;

   // Master compressor/limiter — always in chain as safety limiter
   const masterComp = ctx.createDynamicsCompressor();
   masterComp.threshold.value = -3;
   masterComp.knee.value = 0;
   masterComp.ratio.value = 20;
   masterComp.attack.value = 0.003;
   masterComp.release.value = 0.1;
   let masterCompEnabled = true;
   const masterCompBypass = ctx.createGain();
   masterCompBypass.gain.value = 1;

   const masterAnalyser = ctx.createAnalyser();
   masterAnalyser.fftSize = 256;
   masterAnalyser.smoothingTimeConstant = 0.8;

   // Chain: masterEqLow → masterEqMid → masterEqHi → masterPan → masterGain → masterComp → masterAnalyser → destination
   masterEqLow.connect(masterEqMid);
   masterEqMid.connect(masterEqHi);
   masterEqHi.connect(masterPan);
   masterPan.connect(masterGain);
   // Always run through compressor as a safety limiter
   masterGain.connect(masterComp);
   masterComp.connect(masterAnalyser);
   masterAnalyser.connect(ctx.destination);

   const state = {
      isPlaying: false,
      startTime: 0,       // ctx.currentTime when playback started
      pauseOffset: 0,     // seconds into the project when paused
      bpm: 120,
      tracks: [],         // array of track objects
      scheduledSources: [], // active AudioBufferSourceNodes
      loopEnabled: false,
      loopStart: 0,       // beats
      loopEnd: 4,         // beats
      masterCompEnabled: true,
      masterCompThreshold: -6,
      masterCompRatio: 20,
      masterCompKnee: 0,
      masterCompAttack: 0.003,
      masterCompRelease: 0.1,
      masterVolume: 1.0,
      masterPan: 0,
      masterEqLow: 0,
      masterEqMid: 0,
      masterEqHi: 0,
   };

   // ---- Track management ----
   // Signal flow per track:
   //   source → gainNode(pre-gain) → pannerNode(pre-pan) → [FX chain] → eqLow → eqMid → eqHi
   //         → mixerPan(post-EQ pan) → mixerGain(post-EQ vol) → analyser → masterEqLow
   function createTrack(id) {
      const gainNode = ctx.createGain();          // pre-FX gain (track-list volume)
      gainNode.gain.value = 0.8;                  // default track volume
      const pannerNode = ctx.createStereoPanner(); // pre-FX pan (track-list pan)
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      // FX chain nodes
      const fxInput = ctx.createGain();
      const fxOutput = ctx.createGain();

      // 3-band EQ per track
      const eqLow = ctx.createBiquadFilter();
      eqLow.type = 'lowshelf';
      eqLow.frequency.value = 200;
      eqLow.gain.value = 0;
      const eqMid = ctx.createBiquadFilter();
      eqMid.type = 'peaking';
      eqMid.frequency.value = 1000;
      eqMid.Q.value = 1;
      eqMid.gain.value = 0;
      const eqHi = ctx.createBiquadFilter();
      eqHi.type = 'highshelf';
      eqHi.frequency.value = 5000;
      eqHi.gain.value = 0;

      // Post-EQ mixer controls (controlled by mixer panel)
      const mixerPan = ctx.createStereoPanner();
      const mixerGain = ctx.createGain();
      mixerGain.gain.value = 0.8;

      // Wire the full chain:
      // gainNode → pannerNode → fxInput → fxOutput → eqLow → eqMid → eqHi → mixerPan → mixerGain → analyser → masterEqLow
      gainNode.connect(pannerNode);
      pannerNode.connect(fxInput);
      fxInput.connect(fxOutput); // no FX: direct pass-through
      fxOutput.connect(eqLow);
      eqLow.connect(eqMid);
      eqMid.connect(eqHi);
      eqHi.connect(mixerPan);
      mixerPan.connect(mixerGain);
      mixerGain.connect(analyser);
      analyser.connect(masterEqLow);

      return {
         id,
         gainNode,          // pre-FX gain (track-list volume)
         pannerNode,        // pre-FX pan (track-list pan)
         mixerPan,          // post-EQ pan (mixer)
         mixerGain,         // post-EQ volume (mixer)
         analyser,
         fxInput,
         fxOutput,
         fxEnabled: false,
         fxChain: [],
         eqLow, eqMid, eqHi,
         volume: 0.8,       // pre-FX volume (track-list)
         pan: 0,            // pre-FX pan (track-list)
         mixerVolume: 0.8,  // post-EQ volume (mixer)
         mixerPanVal: 0,    // post-EQ pan (mixer)
         eqLowGain: 0, eqMidGain: 0, eqHiGain: 0,
         muted: false,
         solo: false,
         pitchShift: 0,
         buffer: null,
         acidMeta: null,
         events: [],
      };
   }

   function addTrack(id) {
      const track = createTrack(id);
      state.tracks.push(track);
      return track;
   }

   function removeTrack(id) {
      const idx = state.tracks.findIndex(t => t.id === id);
      if (idx >= 0) {
         const track = state.tracks[idx];
         try { track.gainNode.disconnect(); } catch (_) {}
         state.tracks.splice(idx, 1);
      }
   }

   function getTrack(id) {
      return state.tracks.find(t => t.id === id);
   }

   // ---- Parameter helpers ----
   function setTrackVolume(id, gain) {
      const track = getTrack(id);
      if (track) {
         track.volume = gain;
         track.gainNode.gain.value = track.muted ? 0 : gain;
      }
   }

   function setTrackPan(id, pan) {
      const track = getTrack(id);
      if (track) {
         track.pan = pan;
         track.pannerNode.pan.value = pan / 50;
      }
   }

   function setTrackMute(id, muted) {
      const track = getTrack(id);
      if (track) {
         track.muted = muted;
         track.gainNode.gain.value = muted ? 0 : track.volume;
      }
   }

   // ---- Mixer controls (post-EQ) ----
   function setTrackMixerVolume(id, vol) {
      const track = getTrack(id);
      if (track) {
         track.mixerVolume = vol;
         track.mixerGain.gain.value = vol;
      }
   }

   function setTrackMixerPan(id, pan) {
      const track = getTrack(id);
      if (track) {
         track.mixerPanVal = pan;
         track.mixerPan.pan.value = pan / 50;
      }
   }

   function setTrackFx(id, enabled) {
      const track = getTrack(id);
      if (!track) return;
      track.fxEnabled = enabled && track.fxChain.length > 0;
      // Disconnect fxInput, fxOutput and all FX internal connections
      try { track.fxInput.disconnect(); } catch (_) {}
      try { track.fxOutput.disconnect(); } catch (_) {}
      for (const fx of track.fxChain) {
         // Only disconnect output (breaks inter-FX chain links).
         // NEVER disconnect fx.input - it breaks internal effect connections
         try { fx.output.disconnect(); } catch (_) {}
      }
      // Build chain from non-bypassed effects only
      const activeChain = track.fxChain.filter(fx => !fx._bypassed);
      console.log('[engine] setTrackFx id=' + id + ' enabled=' + enabled + ' chain=' + track.fxChain.length + ' active=' + activeChain.length);
      // Rebuild: fxInput → [active chain or direct] → fxOutput → eqLow
      if (track.fxEnabled && activeChain.length > 0) {
         track.fxInput.connect(activeChain[0].input);
         for (let i = 0; i < activeChain.length - 1; i++) {
            activeChain[i].output.connect(activeChain[i + 1].input);
         }
         activeChain[activeChain.length - 1].output.connect(track.fxOutput);
      } else {
         track.fxInput.connect(track.fxOutput);
      }
      // Always reconnect fxOutput → eqLow
      track.fxOutput.connect(track.eqLow);
   }

   function addTrackFx(id, fxObject) {
      const track = getTrack(id);
      if (!track) return;
      track.fxChain.push(fxObject);
      setTrackFx(id, track.fxChain.length > 0);
   }

   function removeTrackFx(id, index) {
      const track = getTrack(id);
      if (!track) return;
      const fx = track.fxChain[index];
      if (fx && fx.destroy) fx.destroy();
      track.fxChain.splice(index, 1);
      setTrackFx(id, track.fxChain.length > 0);
   }

   function moveTrackFx(id, fromIndex, toIndex) {
      const track = getTrack(id);
      if (!track) return;
      if (fromIndex < 0 || fromIndex >= track.fxChain.length) return;
      if (toIndex < 0 || toIndex >= track.fxChain.length) return;
      const [fx] = track.fxChain.splice(fromIndex, 1);
      track.fxChain.splice(toIndex, 0, fx);
      setTrackFx(id, track.fxChain.length > 0);
   }

   function getTrackFx(id) {
      const track = getTrack(id);
      return track ? track.fxChain : [];
   }

   function setTrackPitch(id, semitones) {
      const track = getTrack(id);
      if (track) track.pitchShift = semitones;
   }

   // ---- Track EQ ----
   function setTrackEq(id, band, gainDb) {
      const track = getTrack(id);
      if (!track) return;
      const clamped = Math.max(-24, Math.min(24, gainDb));
      if (band === 'low') { track.eqLow.gain.value = clamped; track.eqLowGain = clamped; }
      else if (band === 'mid') { track.eqMid.gain.value = clamped; track.eqMidGain = clamped; }
      else if (band === 'hi') { track.eqHi.gain.value = clamped; track.eqHiGain = clamped; }
   }

   // ---- Master controls ----
   function setMasterVolume(vol) {
      masterGain.gain.value = vol;
      state.masterVolume = vol;
   }

   function setMasterPan(pan) {
      masterPan.pan.value = pan / 50;
      state.masterPan = pan;
   }

   function setMasterEq(band, gainDb) {
      const clamped = Math.max(-24, Math.min(24, gainDb));
      if (band === 'low') { masterEqLow.gain.value = clamped; state.masterEqLow = clamped; }
      else if (band === 'mid') { masterEqMid.gain.value = clamped; state.masterEqMid = clamped; }
      else if (band === 'hi') { masterEqHi.gain.value = clamped; state.masterEqHi = clamped; }
   }

   // ---- VU meter: get RMS level for a track (0-1) ----
   function getTrackLevel(id) {
      const track = getTrack(id);
      if (!track || !track.analyser) return 0;
      const data = new Uint8Array(track.analyser.frequencyBinCount);
      track.analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
         const v = (data[i] - 128) / 128;
         sum += v * v;
      }
      return Math.sqrt(sum / data.length);
   }

   function getMasterLevel() {
      if (!masterAnalyser) return 0;
      const data = new Uint8Array(masterAnalyser.frequencyBinCount);
      masterAnalyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
         const v = (data[i] - 128) / 128;
         sum += v * v;
      }
      return Math.sqrt(sum / data.length);
   }

   // ---- Audio buffer loading ----
   async function loadBufferFromArrayBuffer(arrayBuffer) {
      try {
         const buffer = await ctx.decodeAudioData(arrayBuffer);
         return buffer;
      } catch (e) {
         // AIFF fallback (Chrome/Firefox don't support AIFF in decodeAudioData)
         if (isAIFF(arrayBuffer)) {
            try {
               const buffer = await decodeAIFF(ctx, arrayBuffer);
               if (buffer) return buffer;
            } catch (e2) {
               console.error('[dawseur] AIFF decode failed:', e2);
            }
         }
         console.error('[dawseur] decodeAudioData failed:', e);
         return null;
      }
   }

   function setTrackBuffer(id, buffer, acidMeta) {
      const track = getTrack(id);
      if (track) {
         track.buffer = buffer;
         track.acidMeta = acidMeta || null;
      }
   }

   // ---- Transport ----
   function beatsToSeconds(beats, bpm) {
      return (beats / bpm) * 60;
   }

   function secondsToBeats(seconds, bpm) {
      return (seconds / 60) * bpm;
   }

   function play() {
      if (state.isPlaying) return;
      if (ctx.state === 'suspended') ctx.resume();
      state.isPlaying = true;
      state.startTime = ctx.currentTime - state.pauseOffset;
      scheduleAll();
   }

   function stop() {
      state.isPlaying = false;
      state.pauseOffset = 0;
      stopAllSources();
   }

   function pause() {
      if (!state.isPlaying) return;
      state.pauseOffset = getPositionSeconds();
      state.isPlaying = false;
      stopAllSources();
   }

   // Track which events have already been scheduled (by unique ID)
   // to prevent re-scheduling the same event on every scheduler tick
   let scheduledEventIds = new Set();
   let scheduledMIDISteps = new Set(); // track.id:sampleId:stepBeat to prevent re-triggering

   function stopAllSources() {
      for (const src of state.scheduledSources) {
         try { src.stop(0); } catch (_) {}
      }
      state.scheduledSources = [];
      scheduledEventIds.clear();
      scheduledMIDISteps.clear();
   }

   function getPositionSeconds() {
      if (state.isPlaying) {
         return ctx.currentTime - state.startTime;
      }
      return state.pauseOffset;
   }

   function getPositionBeats() {
      let beats = secondsToBeats(getPositionSeconds(), state.bpm);
      // Wrap display position around loop region
      if (state.loopEnabled && state.isPlaying) {
         const loopLen = state.loopEnd - state.loopStart;
         if (loopLen > 0 && beats >= state.loopEnd) {
            // Wrap immediately: subtract exactly loopLen, not modulo
            // This prevents the visual from overshooting past loopStart
            beats = state.loopStart + (beats - state.loopEnd);
            // If still past (multiple wraps), do full modulo
            if (beats >= state.loopEnd) {
               beats = state.loopStart + ((beats - state.loopStart) % loopLen);
            }
         }
      }
      return beats;
   }

   // Get the raw (un-wrapped) position in beats — used internally for scheduling
   function getRawPositionBeats() {
      return secondsToBeats(getPositionSeconds(), state.bpm);
   }

   function setLoop(enabled, startBeat, endBeat) {
      state.loopEnabled = enabled;
      if (startBeat !== undefined) state.loopStart = startBeat;
      if (endBeat !== undefined) state.loopEnd = endBeat;
   }

   function getPositionString() {
      const totalBeats = getPositionBeats();
      const beatsPerMeasure = 4; // simplified from time signature
      const measure = Math.floor(totalBeats / beatsPerMeasure) + 1;
      const beat = Math.floor(totalBeats % beatsPerMeasure) + 1;
      const tick = Math.floor((totalBeats % 1) * 480);
      return `${measure}.${beat}.${String(tick).padStart(3, '0')}`;
   }

   function setBpm(bpm) {
      state.bpm = bpm;
      if (state.isPlaying) {
         // Restart scheduling with new tempo
         state.pauseOffset = getPositionSeconds();
         stopAllSources();
         state.startTime = ctx.currentTime - state.pauseOffset;
         scheduleAll();
      }
   }

   // ---- Scheduling ----
   // ---- Schedule MIDI clip (step sequencer / slicer / piano roll) ----
   // Loads samples from bucket and triggers them at the right times
   function scheduleMIDIClip(event, track, evStartBeat, evEndBeat, startSec, now, lookAhead) {
      const eventVol = event.volume !== undefined ? event.volume : 0.8;
      const secPerBeat = 60 / state.bpm;
      let triggered = false;

      if (event.type === 'stepseq') {
         const seq = event.sequence;
         if (!seq || !seq.channels) return;
         const stepsPerBeat = 4;
         const secPerStep = secPerBeat / stepsPerBeat;
         const seqLenBeats = seq.steps / stepsPerBeat;
         const clipLenBeats = evEndBeat - evStartBeat;
         const numLoops = Math.ceil(clipLenBeats / seqLenBeats);

         for (let ci = 0; ci < seq.channels.length; ci++) {
            const ch = seq.channels[ci];
            if (!ch.sampleId || !ch.steps) continue;

            // Get the buffer for this channel's sample
            const buffer = getMIDISampleBuffer(ch.sampleId);
            if (!buffer) continue;

            // Loop the sequence to fill the clip length
            for (let loop = 0; loop < numLoops; loop++) {
               for (let s = 0; s < seq.steps && s < ch.steps.length; s++) {
                  const stepVal = ch.steps[s];
                  if (stepVal === false || stepVal === undefined || stepVal === null) continue;
                  const stepBeat = evStartBeat + loop * seqLenBeats + s / stepsPerBeat;
                  if (stepBeat >= evEndBeat) break;
                  const stepSec = startSec + beatsToSeconds(stepBeat, state.bpm);
                  if (stepSec < now - 0.05 || stepSec > now + lookAhead) continue;

                  const stepKey = track.id + ':' + ch.sampleId + ':' + stepBeat.toFixed(4);
                  if (scheduledMIDISteps.has(stepKey)) continue;
                  scheduledMIDISteps.add(stepKey);

                  const semitone = typeof stepVal === 'number' ? stepVal : 0;
                  const playbackRate = Math.pow(2, semitone / 12);
                  triggered = true;
                  triggerSample(buffer, track.gainNode, stepSec, ch.volume * eventVol, playbackRate, track.pitchShift || 0);
               }
            }
         }
      } else if (event.type === 'slicer') {
         const seq = event.sequence;
         if (!seq || !seq.slices || !seq.pattern) return;
         const stepsPerBeat = 4;
         const secPerStep = secPerBeat / stepsPerBeat;
         const seqLenBeats = seq.steps / stepsPerBeat;
         const clipLenBeats = evEndBeat - evStartBeat;
         const numLoops = Math.ceil(clipLenBeats / seqLenBeats);

         // Get the buffer for the slicer's sample
         const buffer = getMIDISampleBuffer(seq.sampleId);
         if (!buffer) return;
         const sampleRate = buffer.sampleRate;

         for (let loop = 0; loop < numLoops; loop++) {
            for (let s = 0; s < seq.steps && s < seq.pattern.length; s++) {
               const sliceIndices = seq.pattern[s];
               if (!sliceIndices || sliceIndices.length === 0) continue;
               const stepBeat = evStartBeat + loop * seqLenBeats + s / stepsPerBeat;
               if (stepBeat >= evEndBeat) break;
               const stepSec = startSec + beatsToSeconds(stepBeat, state.bpm);
               if (stepSec < now - 0.05 || stepSec > now + lookAhead) continue;

               const stepKey = track.id + ':' + seq.sampleId + ':' + stepBeat.toFixed(4);
               if (scheduledMIDISteps.has(stepKey)) continue;
               scheduledMIDISteps.add(stepKey);

               for (const si of sliceIndices) {
                  if (si < 0 || si >= seq.slices.length) continue;
                  const slice = seq.slices[si];
                  const offset = slice.start / sampleRate;
                  const duration = (slice.end - slice.start) / sampleRate;
                  const drillKey = s + ':' + si;
                  const subs = (seq.subSteps && seq.subSteps[drillKey]) || 0;
                  if (subs > 1) {
                     // Drill: retrigger subs times evenly within the step
                     const subDur = secPerStep / subs;
                     for (let i = 0; i < subs; i++) {
                        triggered = true;
                        triggerSample(buffer, track.gainNode, stepSec + i * subDur, eventVol, 1.0, track.pitchShift || 0, offset, duration);
                     }
                  } else {
                     triggered = true;
                     triggerSample(buffer, track.gainNode, stepSec, eventVol, 1.0, track.pitchShift || 0, offset, duration);
                  }
               }
            }
         }
      } else if (event.type === 'pianoroll') {
         const pat = event.pattern;
         if (!pat || !pat.notes || !pat.sampleId) return;
         const stepsPerBeat = 4;
         const patLenBeats = (pat.beats || 4);
         const clipLenBeats = evEndBeat - evStartBeat;
         const numLoops = Math.ceil(clipLenBeats / patLenBeats);

         // Get the buffer for the pattern's sample
         const buffer = getMIDISampleBuffer(pat.sampleId);
         if (!buffer) return;

         for (let loop = 0; loop < numLoops; loop++) {
            for (const note of pat.notes) {
               const noteBeat = evStartBeat + loop * patLenBeats + note.startStep / stepsPerBeat;
               if (noteBeat >= evEndBeat) break;
               const noteSec = startSec + beatsToSeconds(noteBeat, state.bpm);
               const noteDurSec = (note.lengthSteps / stepsPerBeat) * secPerBeat;
               if (noteSec + noteDurSec < now - 0.05 || noteSec > now + lookAhead) continue;

               const noteKey = track.id + ':' + pat.sampleId + ':' + noteBeat.toFixed(4) + ':' + note.midi;
               if (scheduledMIDISteps.has(noteKey)) continue;
               scheduledMIDISteps.add(noteKey);

               // playbackRate for pitch shifting (MIDI note -> relative semitone from C4=60)
               const midiNote = note.midi !== undefined ? note.midi : (note.semitone !== undefined ? note.semitone + 60 : 60);
               const playbackRate = Math.pow(2, (midiNote - 60) / 12);
               const pitchShift = track.pitchShift || 0;
               const velocity = (note.velocity !== undefined ? note.velocity : 1.0) * eventVol;
               triggered = true;
               const loopOn = pat.loopEnabled && pat.loopEnd > pat.loopStart;
               if (loopOn) {
                  triggerSampleLooped(buffer, track.gainNode, noteSec, velocity, playbackRate, pitchShift, noteDurSec, pat.adsr, true, pat.loopStart, pat.loopEnd);
               } else if (pat.adsr) {
                  triggerSampleWithADSR(buffer, track.gainNode, noteSec, velocity, playbackRate, pitchShift, 0, noteDurSec, pat.adsr);
               } else {
                  triggerSample(buffer, track.gainNode, noteSec, velocity, playbackRate, pitchShift, 0, noteDurSec);
               }
            }
         }
      }
      return triggered;
   }

   // ---- Trigger a sample at a specific time ----
   function triggerSample(buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, offset, duration) {
      try {
         const src = ctx.createBufferSource();
         src.buffer = buffer;
         src.playbackRate.value = playbackRate;
         src.detune.value = (pitchShiftSemitones || 0) * 100;

         // Clamp start time to now + small buffer (can't schedule in the past)
         const now = ctx.currentTime;
         const startTime = Math.max(whenSec, now + 0.001);

         const gain = ctx.createGain();
         gain.gain.value = volume;
         // Short fade in to avoid clicks; fade out only if duration is specified
         const fadeSec = 0.003;
         gain.gain.setValueAtTime(0, startTime);
         gain.gain.linearRampToValueAtTime(volume, startTime + fadeSec);
         if (duration && duration > fadeSec * 2) {
            gain.gain.setValueAtTime(volume, startTime + duration - fadeSec);
            gain.gain.linearRampToValueAtTime(0, startTime + duration);
         }
         // If no duration, let the sample play in full (no fade-out)

         src.connect(gain);
         gain.connect(destNode);

         const startOffset = offset || 0;
         if (duration) {
            src.start(startTime, startOffset, duration / playbackRate);
         } else {
            src.start(startTime, startOffset);
         }
         state.scheduledSources.push(src);
      } catch (e) {
         console.warn('[engine] triggerSample failed:', e);
      }
   }

   // ---- Monophonic sample trigger: stops the previous source on the same key ----
   // key = unique identifier per voice (e.g. "slicer:0", "stepseq:kick")
   const monoSources = new Map();
   function triggerSampleMono(key, buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, offset, duration) {
      try {
         // Stop previous source on this key
         const prev = monoSources.get(key);
         if (prev) {
            try { prev.stop(whenSec); } catch (e) {}
            monoSources.delete(key);
         }
         const src = ctx.createBufferSource();
         src.buffer = buffer;
         src.playbackRate.value = playbackRate;
         src.detune.value = (pitchShiftSemitones || 0) * 100;

         const now = ctx.currentTime;
         const startTime = Math.max(whenSec, now + 0.001);

         const gain = ctx.createGain();
         gain.gain.value = volume;
         const fadeSec = 0.003;
         gain.gain.setValueAtTime(0, startTime);
         gain.gain.linearRampToValueAtTime(volume, startTime + fadeSec);
         if (duration && duration > fadeSec * 2) {
            gain.gain.setValueAtTime(volume, startTime + duration - fadeSec);
            gain.gain.linearRampToValueAtTime(0, startTime + duration);
         }
         // If no duration, let the sample play in full (mono stop handles cutting)

         src.connect(gain);
         gain.connect(destNode);

         const startOffset = offset || 0;
         if (duration) {
            src.start(startTime, startOffset, duration / playbackRate);
         } else {
            src.start(startTime, startOffset);
         }
         monoSources.set(key, src);
         src.onended = () => { monoSources.delete(key); };
         state.scheduledSources.push(src);
      } catch (e) {
         console.warn('[engine] triggerSampleMono failed:', e);
      }
   }

   // ---- Trigger a sample with ADSR envelope applied ----
   // adsr = { attack, decay, sustain, release } in seconds (sustain 0-1)
   function triggerSampleWithADSR(buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, offset, duration, adsr) {
      try {
         const src = ctx.createBufferSource();
         src.buffer = buffer;
         src.playbackRate.value = playbackRate;
         src.detune.value = (pitchShiftSemitones || 0) * 100;

         const now = ctx.currentTime;
         const startTime = Math.max(whenSec, now + 0.001);

         const a = Math.max(0, adsr ? adsr.attack : 0);
         const d = Math.max(0, adsr ? adsr.decay : 0);
         const s = adsr ? Math.max(0, Math.min(1, adsr.sustain !== undefined ? adsr.sustain : 1)) : 1;
         const r = Math.max(0, adsr ? adsr.release : 0);

         const dur = duration || (buffer.duration / playbackRate);
         const noteOffTime = startTime + dur;

         const gain = ctx.createGain();
         const peak = volume;

         // Attack: 0 -> peak
         gain.gain.setValueAtTime(0, startTime);
         if (a > 0) {
            gain.gain.linearRampToValueAtTime(peak, startTime + a);
         } else {
            gain.gain.setValueAtTime(peak, startTime);
         }

         // Decay: peak -> sustain
         if (d > 0) {
            gain.gain.linearRampToValueAtTime(peak * s, startTime + a + d);
         } else {
            gain.gain.setValueAtTime(peak * s, startTime + a);
         }

         // Sustain held until note off
         // Release: sustain -> 0
         const releaseStart = noteOffTime;
         if (r > 0) {
            gain.gain.setValueAtTime(peak * s, releaseStart);
            gain.gain.linearRampToValueAtTime(0, releaseStart + r);
         } else {
            gain.gain.setValueAtTime(0, releaseStart);
         }

         src.connect(gain);
         gain.connect(destNode);

         const startOffset = offset || 0;
         const totalDur = dur + r;
         src.start(startTime, startOffset, totalDur / playbackRate);

         state.scheduledSources.push(src);
      } catch (e) {
         console.warn('[engine] triggerSampleWithADSR failed:', e);
      }
   }

   // ---- Trigger a sample with optional loop and ADSR ----
   // loopStart/loopEnd are in samples. If loopEnabled, the sample loops between them.
   function triggerSampleLooped(buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, duration, adsr, loopEnabled, loopStart, loopEnd) {
      try {
         const src = ctx.createBufferSource();
         src.buffer = buffer;
         src.playbackRate.value = playbackRate;
         src.detune.value = (pitchShiftSemitones || 0) * 100;

         // Set up loop if enabled and valid
         if (loopEnabled && loopStart >= 0 && loopEnd > loopStart && loopEnd <= buffer.length) {
            src.loop = true;
            src.loopStart = loopStart / buffer.sampleRate;
            src.loopEnd = loopEnd / buffer.sampleRate;
         }

         const now = ctx.currentTime;
         const startTime = Math.max(whenSec, now + 0.001);

         const gain = ctx.createGain();
         const peak = volume;

         if (adsr) {
            const a = Math.max(0, adsr.attack || 0);
            const d = Math.max(0, adsr.decay || 0);
            const s = Math.max(0, Math.min(1, adsr.sustain !== undefined ? adsr.sustain : 1));
            const r = Math.max(0, adsr.release || 0);
            const dur = duration || (buffer.duration / playbackRate);
            const noteOffTime = startTime + dur;

            gain.gain.setValueAtTime(0, startTime);
            if (a > 0) gain.gain.linearRampToValueAtTime(peak, startTime + a);
            else gain.gain.setValueAtTime(peak, startTime);
            if (d > 0) gain.gain.linearRampToValueAtTime(peak * s, startTime + a + d);
            else gain.gain.setValueAtTime(peak * s, startTime + a);
            if (r > 0) {
               gain.gain.setValueAtTime(peak * s, noteOffTime);
               gain.gain.linearRampToValueAtTime(0, noteOffTime + r);
            } else {
               gain.gain.setValueAtTime(0, noteOffTime);
            }

            src.connect(gain);
            gain.connect(destNode);
            const totalDur = dur + r;
            src.start(startTime, 0, totalDur / playbackRate);
         } else {
            const fadeSec = 0.003;
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(peak, startTime + fadeSec);
            if (duration && duration > fadeSec * 2) {
               gain.gain.setValueAtTime(peak, startTime + duration - fadeSec);
               gain.gain.linearRampToValueAtTime(0, startTime + duration);
            } else {
               gain.gain.linearRampToValueAtTime(0, startTime + fadeSec + 0.05);
            }
            src.connect(gain);
            gain.connect(destNode);
            if (duration) {
               src.start(startTime, 0, duration / playbackRate);
            } else {
               src.start(startTime, 0);
            }
         }

         // src.start() with duration already stops the source at note end.
         // No explicit src.stop() needed — the duration parameter handles it.
         // The source loops between loopStart/loopEnd until the duration expires.

         state.scheduledSources.push(src);
      } catch (e) {
         console.warn('[engine] triggerSampleLooped failed:', e);
      }
   }

   // ---- Cache for MIDI sample buffers ----
   const midiSampleCache = new Map();
   let midiSampleLoader = null; // set by dawseur.js

   function setMIDISampleLoader(loader) {
      midiSampleLoader = loader;
   }

   // ---- Get a decoded AudioBuffer for a bucket sample ID ----
   function getMIDISampleBuffer(sampleId) {
      if (!sampleId) return null;
      if (midiSampleCache.has(sampleId)) return midiSampleCache.get(sampleId);
      // Asynchronously load and cache
      if (midiSampleLoader) {
         midiSampleLoader(sampleId).then(buf => {
            if (buf) midiSampleCache.set(sampleId, buf);
         }).catch(() => {});
      }
      return null; // will be available on next scheduler tick
   }

   function scheduleAll() {
      const lookAhead = 4.0; // schedule 4 seconds ahead
      const now = ctx.currentTime;
      const startSec = state.startTime;

      for (const track of state.tracks) {
         if (track.muted) continue;
         // Check if any soloed track exists
         const anySolo = state.tracks.some(t => t.solo);
         if (anySolo && !track.solo) continue;

         for (const event of track.events) {
            // ---- MIDI clip types (step sequencer / slicer / piano roll) ----
            if (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll') {
               const eventId = track.id + ':' + event.type + ':' + event.startBeat + ':' + event.lengthBeats;
               if (scheduledEventIds.has(eventId)) continue;

               let evStartBeat = event.startBeat;
               let evEndBeat = event.startBeat + event.lengthBeats;
               if (state.loopEnabled) {
                  if (evEndBeat <= state.loopStart || evStartBeat >= state.loopEnd) continue;
                  if (evStartBeat < state.loopStart) evStartBeat = state.loopStart;
                  if (evEndBeat > state.loopEnd) evEndBeat = state.loopEnd;
               }
               const eventStartSec = startSec + beatsToSeconds(evStartBeat, state.bpm);
               const eventEndSec = startSec + beatsToSeconds(evEndBeat, state.bpm);
               if (eventEndSec < now || eventStartSec > now + lookAhead) continue;

               // MIDI clips: don't mark as fully scheduled — they need to be
               // re-scheduled every tick because steps span multiple ticks.
               // The time window check (stepSec < now - 0.05 || stepSec > now + lookAhead)
               // inside scheduleMIDIClip prevents double-triggering.
               scheduleMIDIClip(event, track, evStartBeat, evEndBeat, startSec, now, lookAhead);
               continue;
            }

            // Each event has its own buffer (supports multiple samples per track)
            const eventBuffer = event.buffer || track.buffer;
            if (!eventBuffer) continue;

            // Skip if this event was already scheduled (prevents overlapping sources)
            const eventId = track.id + ':' + event.startBeat + ':' + event.lengthBeats;
            if (scheduledEventIds.has(eventId)) continue;

            let evStartBeat = event.startBeat;
            let evEndBeat = event.startBeat + event.lengthBeats;

            // When looping, only schedule events that overlap the loop region
            // and clamp them to the loop boundaries
            if (state.loopEnabled) {
               if (evEndBeat <= state.loopStart || evStartBeat >= state.loopEnd) continue;
               if (evStartBeat < state.loopStart) evStartBeat = state.loopStart;
               if (evEndBeat > state.loopEnd) evEndBeat = state.loopEnd;
            }

            const eventStartSec = startSec + beatsToSeconds(evStartBeat, state.bpm);
            const eventEndSec = startSec + beatsToSeconds(evEndBeat, state.bpm);

            // Skip events entirely in the past
            if (eventEndSec < now) continue;
            // Skip events too far in the future
            if (eventStartSec > now + lookAhead) continue;

            // Per-event stretch ratio: how much to stretch the buffer to fit the event length
            // When event.stretched is true: stretch to fit (WSOLA time-stretch)
            // When event.stretched is false: play at natural speed (ratio=1.0), loop or cut
            const bufferDuration = eventBuffer.duration;
            if (!bufferDuration || !isFinite(bufferDuration) || bufferDuration <= 0) continue;

            const eventDurationSec = eventEndSec - eventStartSec;
            if (eventDurationSec <= 0) continue;

            // stretchRatio > 1 = buffer shorter than event (slow down to fill)
            // stretchRatio < 1 = buffer longer than event (speed up to fit)
            const isStretched = event.stretched === true;
            const stretchRatio = isStretched ? (eventDurationSec / bufferDuration) : 1.0;
            // Clamp to reasonable range to avoid extreme artifacts
            const actualStretchRatio = Math.max(0.25, Math.min(4.0, stretchRatio));

            const stretchedDuration = bufferDuration * actualStretchRatio;
            if (stretchedDuration <= 0 || !isFinite(stretchedDuration)) continue;

            // Per-event gain node for fade in/out + volume
            // Chain: sourceGain → fadeInGain → fadeOutGain → track.gainNode
            const eventVol = event.volume !== undefined ? event.volume : 0.5;
            const fadeInGain = ctx.createGain();
            const fadeOutGain = ctx.createGain();
            fadeInGain.connect(fadeOutGain);
            fadeOutGain.connect(track.gainNode);

            // Fade in automation
            const fadeInBeats = event.fadeInBeats || 0;
            const fadeOutBeats = event.fadeOutBeats || 0;
            let fadeInSec = beatsToSeconds(fadeInBeats, state.bpm);
            let fadeOutSec = beatsToSeconds(fadeOutBeats, state.bpm);
            // Clamp fades so they don't overlap
            const eventDur = eventEndSec - eventStartSec;
            if (fadeInSec + fadeOutSec > eventDur) {
               const total = fadeInSec + fadeOutSec;
               if (total > 0) {
                  fadeInSec = eventDur * (fadeInSec / total);
                  fadeOutSec = eventDur * (fadeOutSec / total);
               }
            }

            // Helper: Catmull-Rom spline through 4 points — handles sit ON the curve
            function splineGainAtX(xTarget, cp1x, cp1y, cp2x, cp2y, y0, y3) {
               const pts = [[0, y0], [cp1x, cp1y], [cp2x, cp2y], [1, y3]];
               const pm1 = [2*pts[0][0] - pts[1][0], 2*pts[0][1] - pts[1][1]];
               const p4  = [2*pts[3][0] - pts[2][0], 2*pts[3][1] - pts[2][1]];
               const all = [pm1, pts[0], pts[1], pts[2], pts[3], p4];
               const samples = 60;
               const table = [];
               for (let seg = 0; seg < 3; seg++) {
                  const a = all[seg], b = all[seg+1], c = all[seg+2], d = all[seg+3];
                  const b1 = [b[0] + (c[0] - a[0]) / 6, b[1] + (c[1] - a[1]) / 6];
                  const b2 = [c[0] - (d[0] - b[0]) / 6, c[1] - (d[1] - b[1]) / 6];
                  for (let i = 0; i < samples; i++) {
                     const t = i / samples, mt = 1 - t;
                     const x = mt*mt*mt*b[0] + 3*mt*mt*t*b1[0] + 3*mt*t*t*b2[0] + t*t*t*c[0];
                     const y = mt*mt*mt*b[1] + 3*mt*mt*t*b1[1] + 3*mt*t*t*b2[1] + t*t*t*c[1];
                     table.push([x, y]);
                  }
               }
               table.push([1, y3]);
               table.sort((p, q) => p[0] - q[0]);
               if (xTarget <= table[0][0]) return table[0][1];
               if (xTarget >= table[table.length-1][0]) return table[table.length-1][1];
               let lo = 0, hi = table.length - 1;
               while (hi - lo > 1) {
                  const mid = (lo + hi) >> 1;
                  if (table[mid][0] < xTarget) lo = mid;
                  else hi = mid;
               }
               const f = (xTarget - table[lo][0]) / (table[hi][0] - table[lo][0]);
               return table[lo][1] + f * (table[hi][1] - table[lo][1]);
            }

            // Fade in gain: 0→1 curve (on fadeInGain), multiplied by eventVol via fadeOutGain base
            // fadeInGain: curve 0→1
            // fadeOutGain: base = eventVol, then curve 1→0 during fade out
            fadeOutGain.gain.value = eventVol;

            if (fadeInSec > 0.001) {
               const startTime = Math.max(eventStartSec, now);
               const cp1x = Math.max(0, Math.min(1, event.fadeInCp1X !== undefined ? event.fadeInCp1X : 1/3));
               const cp1y = Math.max(0, Math.min(1, event.fadeInCp1Y !== undefined ? event.fadeInCp1Y : 1/3));
               const cp2x = Math.max(0, Math.min(1, event.fadeInCp2X !== undefined ? event.fadeInCp2X : 2/3));
               const cp2y = Math.max(0, Math.min(1, event.fadeInCp2Y !== undefined ? event.fadeInCp2Y : 2/3));
               const steps = 64;
               const curve = new Float32Array(steps);
               for (let i = 0; i < steps; i++) {
                  const xTarget = i / (steps - 1);
                  const gain = splineGainAtX(xTarget, cp1x, cp1y, cp2x, cp2y, 0, 1);
                  curve[i] = Math.max(0.0001, Math.min(1, gain));
               }
               try {
                  fadeInGain.gain.setValueCurveAtTime(curve, startTime, fadeInSec);
               } catch (e) {
                  fadeInGain.gain.setValueAtTime(0.0001, startTime);
                  fadeInGain.gain.linearRampToValueAtTime(1, startTime + fadeInSec);
               }
            } else {
               fadeInGain.gain.value = 1;
            }

            // Fade out gain: 1→0 curve (on fadeOutGain, starting from eventVol)
            if (fadeOutSec > 0.001) {
               const fadeOutStart = eventEndSec - fadeOutSec;
               const cp1x = Math.max(0, Math.min(1, event.fadeOutCp1X !== undefined ? event.fadeOutCp1X : 1/3));
               const cp1y = Math.max(0, Math.min(1, event.fadeOutCp1Y !== undefined ? event.fadeOutCp1Y : 2/3));
               const cp2x = Math.max(0, Math.min(1, event.fadeOutCp2X !== undefined ? event.fadeOutCp2X : 2/3));
               const cp2y = Math.max(0, Math.min(1, event.fadeOutCp2Y !== undefined ? event.fadeOutCp2Y : 1/3));
               const steps = 64;
               const curve = new Float32Array(steps);
               for (let i = 0; i < steps; i++) {
                  const xTarget = i / (steps - 1);
                  const gain = splineGainAtX(xTarget, cp1x, cp1y, cp2x, cp2y, 1, 0);
                  curve[i] = Math.max(0.0001, Math.min(1, gain)) * eventVol;
               }
               // Set explicit value before curve to ensure clean automation state
               const curveStart = Math.max(fadeOutStart, now);
               const curveDur = eventEndSec - curveStart;
               if (curveDur > 0.001) {
                  // Adjust curve length if we're starting mid-fade-out
                  let useCurve = curve;
                  if (curveDur < fadeOutSec - 0.01) {
                     // We're starting partway through the fade out — scale the curve
                     const startFrac = 1 - (curveDur / fadeOutSec);
                     const startIdx = Math.floor(startFrac * (steps - 1));
                     const subSteps = steps - startIdx;
                     useCurve = new Float32Array(subSteps);
                     for (let i = 0; i < subSteps; i++) {
                        useCurve[i] = curve[startIdx + i];
                     }
                  }
                  try {
                     fadeOutGain.gain.setValueAtTime(useCurve[0], curveStart);
                     fadeOutGain.gain.setValueCurveAtTime(useCurve, curveStart, curveDur);
                  } catch (e) {
                     fadeOutGain.gain.setValueAtTime(eventVol, curveStart);
                     fadeOutGain.gain.linearRampToValueAtTime(0.0001, eventEndSec);
                  }
               }
            }

            // ---- SoundTouch WSOLA time-stretch scheduling ----
            // Pre-stretch the buffer offline using SoundTouch's WSOLA algorithm.
            // The stretched buffer is cached on the event and reused.
            // Playback uses normal BufferSourceNode with native looping.
            const playStart = Math.max(eventStartSec, now);
            const playEnd = eventEndSec;
            const playDuration = playEnd - playStart;
            if (playDuration <= 0) continue;

            // Check cache: does the event have a pre-stretched buffer for this ratio?
            // When not stretched (ratio=1.0), use the original buffer directly — no WSOLA needed
            const cacheKey = Math.round(actualStretchRatio * 1000) / 1000; // 3 decimal precision
            if (!isStretched) {
               // Natural speed: use original buffer, loop if event is longer than buffer
               event._stretchedCache = eventBuffer;
               event._stretchedCacheKey = cacheKey;
            } else if (!event._stretchedCache || event._stretchedCacheKey !== cacheKey) {
               // Stretch the buffer offline (pitch-preserving WSOLA)
               try {
                  event._stretchedCache = stretchBufferOffline(ctx, eventBuffer, actualStretchRatio);
                  event._stretchedCacheKey = cacheKey;
               } catch (e) {
                  console.warn('[dawseur] stretch failed, using original:', e);
                  event._stretchedCache = eventBuffer;
                  event._stretchedCacheKey = cacheKey;
               }
            }

            const playBuffer = event._stretchedCache;
            const playBufferDuration = playBuffer.duration;

            // Single looping BufferSource with the pre-stretched buffer
            const source = ctx.createBufferSource();
            source.buffer = playBuffer;
            source.playbackRate.value = 1.0;
            source.detune.value = track.pitchShift * 100;
            source.loop = true;
            source.loopStart = 0;
            // Use the actual audio end (before silence padding) for seamless looping
            source.loopEnd = event._bakedAudioEndSec || playBufferDuration;

            // Calculate buffer offset for mid-event scheduling
            const offsetIntoEvent = playStart - eventStartSec;
            const bufferOffset = (offsetIntoEvent % playBufferDuration);

            // Per-source gain with short fade in/out to avoid clicks.
            // Only apply fade-in when starting genuinely mid-event (prevents click).
            // When starting at/near the event start, no fade-in needed —
            // the buffer starts at offset 0 which is clean.
            // Use 100ms threshold to cover timing jitter from ctx.currentTime
            // being called twice (once in play(), once in scheduleAll()).
            const sourceGain = ctx.createGain();
            sourceGain.connect(fadeInGain);
            const srcFadeInSec = 0.003;
            const srcFadeOutSec = 0.003;
            const needsFadeIn = (offsetIntoEvent > 0.1 && bufferOffset > 0.01);
            if (needsFadeIn) {
               sourceGain.gain.setValueAtTime(0, playStart);
               sourceGain.gain.linearRampToValueAtTime(1, playStart + srcFadeInSec);
            } else {
               sourceGain.gain.setValueAtTime(1, playStart);
            }
            sourceGain.gain.setValueAtTime(1, playEnd - srcFadeOutSec);
            sourceGain.gain.linearRampToValueAtTime(0, playEnd);
            source.connect(sourceGain);

            try {
               source.start(playStart, bufferOffset, playDuration);
               state.scheduledSources.push(source);
               scheduledEventIds.add(eventId);
               source.onended = () => {
                  const idx = state.scheduledSources.indexOf(source);
                  if (idx >= 0) state.scheduledSources.splice(idx, 1);
                  setTimeout(() => {
                     try { sourceGain.disconnect(); } catch (_) {}
                     try { fadeInGain.disconnect(); } catch (_) {}
                     try { fadeOutGain.disconnect(); } catch (_) {}
                  }, 100);
               };
            } catch (e) {}
         }
      }
   }

   // ---- Reschedule (called when events change during playback) ----
   let rescheduleTimer = null;
   function reschedule() {
      if (!state.isPlaying) return;
      // Debounce: only reschedule once per 100ms to avoid thrashing during drag
      if (rescheduleTimer) return;
      rescheduleTimer = setTimeout(() => {
         rescheduleTimer = null;
         if (!state.isPlaying) return;
         stopAllSources();
         state.pauseOffset = getPositionSeconds();
         state.startTime = ctx.currentTime - state.pauseOffset;
         scheduleAll();
      }, 100);
   }

   // ---- Continuous scheduler ----
   let schedulerInterval = null;
   let lastLoopWrapTime = 0;
   function startScheduler() {
      if (schedulerInterval) clearInterval(schedulerInterval);
      // Run every 25ms for tighter loop wrapping (less visual overshoot)
      schedulerInterval = setInterval(() => {
         if (!state.isPlaying) return;

         // Check if we need to wrap the loop
         if (state.loopEnabled) {
            const currentBeats = secondsToBeats(getPositionSeconds(), state.bpm);
            const loopLen = state.loopEnd - state.loopStart;
            if (loopLen > 0 && currentBeats >= state.loopEnd) {
               // Jump startTime forward by the loop length so the scheduler
               // re-schedules all events within the loop region
               const loopLenSec = beatsToSeconds(loopLen, state.bpm);
               state.startTime += loopLenSec;
               // Stop sources that were scheduled beyond the loop point
               stopAllSources();
               scheduleAll();
               lastLoopWrapTime = ctx.currentTime;
               return;
            }
         }

         // If not looping, check if we've passed the last event — restart from beginning
         if (!state.loopEnabled) {
            const currentBeats = secondsToBeats(getPositionSeconds(), state.bpm);
            // Find the last event end beat across all tracks
            let lastBeat = 0;
            for (const track of state.tracks) {
               for (const event of track.events) {
                  if (!event.buffer) continue;
                  const endBeat = event.startBeat + event.lengthBeats;
                  if (endBeat > lastBeat) lastBeat = endBeat;
               }
            }
            // If there are events and we're past the last one, restart from beat 0
            if (lastBeat > 0 && currentBeats >= lastBeat + 0.5) {
               state.startTime = ctx.currentTime;
               state.pauseOffset = 0;
               stopAllSources();
               scheduleAll();
               return;
            }
         }

         scheduleAll();
      }, 25);
   }

   startScheduler();

   // ---- Preview (audition a buffer without adding to timeline) ----
   function previewBuffer(buffer, opts = {}) {
      const stretchRatio = opts.tempo ? opts.tempo / state.bpm : 1;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = 1 / stretchRatio;
      if (opts.pitchShift) source.detune.value = opts.pitchShift * 100;
      const gain = ctx.createGain();
      gain.gain.value = 0.8;
      source.connect(gain);
      gain.connect(masterGain);
      source.start(0);
      return source;
   }

   function setMasterComp(enabled) {
      masterCompEnabled = enabled;
      state.masterCompEnabled = enabled;
      try {
         masterGain.disconnect();
         masterCompBypass.disconnect();
         masterComp.disconnect();
      } catch (_) {}
      if (enabled) {
         masterGain.connect(masterComp);
         masterComp.connect(masterAnalyser);
      } else {
         masterGain.connect(masterCompBypass);
         masterCompBypass.connect(masterAnalyser);
      }
   }

   function setMasterCompParam(param, value) {
      state['masterComp' + param.charAt(0).toUpperCase() + param.slice(1)] = value;
      if (param === 'threshold') masterComp.threshold.value = value;
      else if (param === 'ratio') masterComp.ratio.value = value;
      else if (param === 'knee') masterComp.knee.value = value;
      else if (param === 'attack') masterComp.attack.value = value;
      else if (param === 'release') masterComp.release.value = value;
   }

   return {
      ctx,
      masterGain,
      state,
      addTrack,
      removeTrack,
      getTrack,
      setTrackVolume,
      setTrackPan,
      setTrackMute,
      setTrackMixerVolume,
      setTrackMixerPan,
      setTrackPitch,
      setTrackEq,
      setMasterVolume,
      setMasterComp,
      setMasterCompParam,
      setMasterPan,
      setMasterEq,
      setTrackFx,
      addTrackFx,
      removeTrackFx,
      moveTrackFx,
      getTrackFx,
      setTrackBuffer,
      setMIDISampleLoader,
      clearMIDISampleCache: () => midiSampleCache.clear(),
      getTrackLevel,
      getMasterLevel,
      loadBufferFromArrayBuffer,
      play,
      stop,
      pause,
      reschedule,
      setBpm,
      setLoop,
      getPositionSeconds,
      getPositionBeats,
      getPositionString,
      previewBuffer,
      getMIDISampleBuffer,
      setMIDISampleBuffer: (id, buffer) => midiSampleCache.set(id, buffer),
      triggerSample,
      triggerSampleMono,
      triggerSampleWithADSR,
      triggerSampleLooped,
   };
}

module.exports = { createPlaybackEngine, stretchBufferOffline };
